import { useState, useEffect } from 'react';
import { DetectionType, Alert } from '../types/camera';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://csense.airivu.in';
const SNAPSHOT_BASE_URL = import.meta.env.VITE_SNAPSHOT_BASE_URL || 'https://csense.airivu.in';

// Unified detection service endpoint
const UNIFIED_DETECTION_ENDPOINT = import.meta.env.VITE_UNIFIED_DETECTION_ENDPOINT || 'https://csense.airivu.in';

// Authentication token management
let authToken: string | null = null;

export const setAuthToken = (token: string) => {
  authToken = token;
  localStorage.setItem('csense_auth_token', token);
};

export const getAuthToken = (): string | null => {
  if (authToken) return authToken;
  authToken = localStorage.getItem('csense_auth_token');
  return authToken;
};

export const clearAuthToken = () => {
  authToken = null;
  localStorage.removeItem('csense_auth_token');
};

export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

// API request helper with authentication
export const apiRequest = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_BASE_URL}${endpoint}`;

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 401) {
        if (endpoint.includes('/auth/login')) {
          throw new Error(errorData.detail || 'Invalid credentials. Please check your email and password.');
        } else if (endpoint.includes('/auth/refresh')) {
          throw new Error('Token refresh failed');
        } else {
          clearAuthToken();
          throw new Error('Session expired. Please log in again.');
        }
      }

      if (response.status === 404) {
        throw new Error(errorData.detail || 'Account not found. Please sign up first.');
      }

      if (response.status === 400) {
        throw new Error(errorData.detail || 'Bad request. Please check your input.');
      }

      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

// Authentication interfaces
export interface SignupData {
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  password: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    phone?: string;
    is_active: boolean;
    created_at: string;
    provider: string;
    onboarding_completed: boolean;
    camera_count: number;
  };
}

// Authentication API
export const authAPI = {
  signup: async (userData: SignupData): Promise<AuthResponse> => {
    return apiRequest('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  login: async (credentials: LoginData): Promise<AuthResponse> => {
    return apiRequest('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },

  // Google OAuth login removed - using email/password authentication only

  getCurrentUser: async () => {
    return apiRequest('/api/auth/me');
  },

  completeOnboarding: async (orgData?: any) => {
    return apiRequest('/api/user/complete-onboarding', {
      method: 'POST',
      body: JSON.stringify(orgData || {})
    });
  },

  addCamera: async (cameraData: any) => {
    return apiRequest('/api/user/cameras', {
      method: 'POST',
      body: JSON.stringify(cameraData)
    });
  },

  getUserCameras: async () => {
    return apiRequest('/api/user/cameras');
  },

  refreshToken: async () => {
    return apiRequest('/api/auth/refresh', {
      method: 'POST'
    });
  },

  // Agent management endpoints
  generatePairCode: async () => {
    return apiRequest('/api/agent/generate-pair-code', {
      method: 'POST'
    });
  },

  getAgentStatus: async () => {
    return apiRequest('/api/agents/status');
  },

  updateProfile: async (profileData: any) => {
    return apiRequest('/api/auth/me', {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  },

  sendAgentCommand: async (agentId: string, command: any) => {
    return apiRequest('/api/agent/command', {
      method: 'POST',
      body: JSON.stringify({ agent_id: agentId, command })
    });
  },

  getCameraSnapshot: async (cameraId: string, streamUrl: string) => {
    const response = await fetch(`${SNAPSHOT_BASE_URL}/api/camera/snapshot/${cameraId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        streamUrl: streamUrl
      })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get snapshot: ${response.statusText}`);
    }
    
    return response.blob();
  },

  // Camera Discovery API
  discoverCameras: async (config: {
    networkRange: string;
    scanPorts: number[];
    testCredentials: boolean;
    onvifDiscovery: boolean;
  }) => {
    return apiRequest('/api/cameras/discover-advanced', {
      method: 'POST',
      body: JSON.stringify(config)
    });
  },

  bulkAddCameras: async (cameras: any[]) => {
    return apiRequest('/api/cameras/bulk-add', {
      method: 'POST',
      body: JSON.stringify(cameras)
    });
  },

  // Enhanced camera management
  getCameras: async () => {
    return apiRequest('/api/cameras');
  },

  getCamera: async (id: number) => {
    return apiRequest(`/api/cameras/${id}`);
  },

  updateCamera: async (id: number, data: any) => {
    return apiRequest(`/api/cameras/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteCamera: async (id: number) => {
    return apiRequest(`/api/cameras/${id}`, {
      method: 'DELETE'
    });
  },

  resolveCameraIP: async (id: number) => {
    return apiRequest(`/api/cameras/${id}/resolve-ip`, {
      method: 'POST'
    });
  },

  getCameraHealth: async (id: number) => {
    return apiRequest(`/api/cameras/${id}/health`);
  },

  getCameraIPChanges: async (id: number) => {
    return apiRequest(`/api/cameras/${id}/ip-changes`);
  },

  startCameraMonitoring: async () => {
    return apiRequest('/api/cameras/start-monitoring', {
      method: 'POST'
    });
  },

  stopCameraMonitoring: async () => {
    return apiRequest('/api/cameras/stop-monitoring', {
      method: 'POST'
    });
  },
};

export interface Camera {
  id: string;
  name: string;
  streamUrl: string;
  detectionType: 'fall-detection' | 'fire-smoke' | 'anpr';
  detectionActive?: boolean;
  location?: string;
  status?: 'online' | 'offline' | 'error';
  ip?: string;
  port?: number;
  username?: string;
  password?: string;
}

class ApiService {

  // Start detection using the coordinated backend
  async startDetection(
    cameraUrl: string, 
    streamId: string, 
    detectionType: DetectionType
  ): Promise<any> {
    const response = await apiRequest('/api/detection/start', {
      method: 'POST',
      body: JSON.stringify({
        stream_url: cameraUrl,
        camera_id: streamId,
        detection_type: detectionType
      })
    });
    
    return response;
  }

  // Stop detection using the coordinated backend
  async stopDetection(streamId: string): Promise<any> {
    const response = await apiRequest(`/api/detection/stop/${streamId}`, {
      method: 'POST'
    });
    
    return response;
  }

  // Get detection status
  async getDetectionStatus(): Promise<any> {
    return await apiRequest('/api/detection/status');
  }

  // Check detection service health
  async getDetectionHealth(): Promise<any> {
    return await apiRequest('/api/detection/health');
  }

  // Get unified detection service health
  async getHealth(): Promise<any> {
    try {
      // Use the main backend health endpoint instead of direct detection service
      const response = await apiRequest('/api/health');
      return { status: 'healthy', data: response };
    } catch (error) {
      return { status: 'error', error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Check unified detection service health
  async checkDetectionHealth(): Promise<boolean> {
    try {
      const response = await apiRequest('/api/health');
      return !!response;
    } catch {
      return false;
    }
  }

  // Get alerts for specific camera/detection type (requires authentication)
  async getAlerts(_cameraId?: string, _detectionType?: DetectionType): Promise<Alert[]> {
    try {
      const response = await apiRequest('/api/alerts');
      return Array.isArray(response.alerts) ? response.alerts : [];
    } catch (error) {
      throw new Error(`Failed to fetch alerts: ${error}`);
    }
  }

  // Add alert (called by detection services)
  async addAlert(alert: Omit<Alert, 'id' | 'timestamp'>): Promise<Alert> {
    const response = await fetch(`${API_BASE_URL}/alerts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...alert,
        timestamp: new Date().toISOString()
      }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to add alert');
    }
    
    return response.json();
  }

  // Get unified detection service stats
  async getStats(): Promise<any> {
    try {
      return await apiRequest('/api/stats');
    } catch (error) {
      throw new Error(`Failed to get stats: ${error}`);
    }
  }

  // Get unified detection stats
  async getDetectionStats() {
    return await apiRequest('/api/stats');
  }

  // Edge Device Management API
  async getEdgeDevices() {
    return await apiRequest('/api/edge/devices');
  }

  async getPendingEdgeDevices() {
    return await apiRequest('/api/edge/devices/pending');
  }

  async approveEdgeDevice(deviceId: string) {
    return await apiRequest(`/api/edge/devices/${deviceId}/approve`, {
      method: 'POST'
    });
  }

  async rejectEdgeDevice(deviceId: string, reason?: string) {
    return await apiRequest(`/api/edge/devices/${deviceId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || '' })
    });
  }

  async toggleEdgeDeviceSync(deviceId: string, enable: boolean) {
    return await apiRequest(`/api/edge/devices/${deviceId}/sync`, {
      method: 'POST',
      body: JSON.stringify({ enable })
    });
  }

  async getEdgeDeviceStatus(deviceId: string) {
    return await apiRequest(`/api/edge/status/${deviceId}`);
  }

  async sendEdgeHeartbeat(deviceId: string, status: string) {
    return await apiRequest('/api/edge/heartbeat', {
      method: 'POST',
      body: JSON.stringify({ device_id: deviceId, status })
    });
  }

  async pushEdgeAlert(alertData: any) {
    return await apiRequest('/api/edge/alerts/push', {
      method: 'POST',
      body: JSON.stringify(alertData)
    });
  }

  async getEdgeConfig(deviceId: string) {
    return await apiRequest(`/api/edge/sync/config?device_id=${deviceId}`);
  }

  // Restricted Area PH Zone Management
  async setRestrictedAreaZone(cameraId: string, polygonPoints: number[][]) {
    return await apiRequest(`/api/restricted-area-ph/set-zone/${cameraId}`, {
      method: 'POST',
      body: JSON.stringify({ polygon_points: polygonPoints })
    });
  }

  async getRestrictedAreaZone(cameraId: string) {
    return await apiRequest(`/api/restricted-area-ph/get-zone/${cameraId}`);
  }

  // Zone-Based Intrusion Detection Zone Management
  async setIntrusionZone(cameraId: string, polygonPoints: number[][]) {
    return await apiRequest(`/api/detection/zone-intrusion/set-zone/${cameraId}`, {
      method: 'POST',
      body: JSON.stringify({ polygon_points: polygonPoints })
    });
  }

  async getIntrusionZone(cameraId: string) {
    return await apiRequest(`/api/detection/zone-intrusion/get-zone/${cameraId}`);
  }

  // Intrusion Safe Zone Management (alias for zone-based intrusion)
  async setIntrusionSafeZone(cameraId: string, polygonPoints: number[][]) {
    return this.setIntrusionZone(cameraId, polygonPoints);
  }

  async getIntrusionSafeZone(cameraId: string) {
    return this.getIntrusionZone(cameraId);
  }
}

export const apiService = new ApiService();

// Individual function exports for zone management
export const setIntrusionZone = async (cameraId: string, polygonPoints: number[][]) => {
  return await apiRequest(`/api/detection/zone-intrusion/set-zone/${cameraId}`, {
    method: 'POST',
    body: JSON.stringify({ polygon_points: polygonPoints })
  });
};

export const getIntrusionZone = async (cameraId: string) => {
  return await apiRequest(`/api/detection/zone-intrusion/get-zone/${cameraId}`);
};

export const setIntrusionSafeZone = async (cameraId: string, polygonPoints: number[][]) => {
  return await apiRequest(`/api/detection/zone-intrusion/set-zone/${cameraId}`, {
    method: 'POST',
    body: JSON.stringify({ polygon_points: polygonPoints })
  });
};

export const getIntrusionSafeZone = async (cameraId: string) => {
  return await apiRequest(`/api/detection/zone-intrusion/get-zone/${cameraId}`);
};

// Export zone management functions for easy access
export const zoneAPI = {
  // Restricted Area PH
  setRestrictedAreaZone: (cameraId: string, polygonPoints: number[][]) => 
    apiService.setRestrictedAreaZone(cameraId, polygonPoints),
  getRestrictedAreaZone: (cameraId: string) => 
    apiService.getRestrictedAreaZone(cameraId),
  
  // Zone-Based Intrusion Detection
  setIntrusionZone: async (cameraId: string, polygonPoints: number[][]) => {
    return await apiRequest(`/api/detection/zone-intrusion/set-zone/${cameraId}`, {
      method: 'POST',
      body: JSON.stringify({ polygon_points: polygonPoints })
    });
  },
  getIntrusionZone: async (cameraId: string) => {
    return await apiRequest(`/api/detection/zone-intrusion/get-zone/${cameraId}`);
  },
  
  // Intrusion Safe Zone (alias for zone-based intrusion)
  setIntrusionSafeZone: async (cameraId: string, polygonPoints: number[][]) => {
    return await apiRequest(`/api/detection/zone-intrusion/set-zone/${cameraId}`, {
      method: 'POST',
      body: JSON.stringify({ polygon_points: polygonPoints })
    });
  },
  getIntrusionSafeZone: async (cameraId: string) => {
    return await apiRequest(`/api/detection/zone-intrusion/get-zone/${cameraId}`);
  }
};

// React hooks for the components
export function useApiHealth() {
  const [health, setHealth] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        setLoading(true);
        const healthData = await apiService.getHealth();
        setHealth(healthData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch health');
        setHealth(null);
      } finally {
        setLoading(false);
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Add isHealthy property for backwards compatibility
  const isHealthy = health && Array.isArray(health) 
    ? health.some(h => h.status === 'healthy')
    : health?.status === 'healthy';

  return { health, loading, error, isHealthy };
}

export function useDetectionStats() {
  const [stats, setStats] = useState<Record<DetectionType, any>>({
    'fall-detection': null,
    'anpr': null,
    'fire-smoke': null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const statsData = await apiService.getStats();
        
        if (Array.isArray(statsData)) {
          const statsObj: Record<DetectionType, any> = {
            'fall-detection': null,
            'anpr': null,
            'fire-smoke': null
          };
          
          statsData.forEach(({ type, data }) => {
            if (type in statsObj) {
              statsObj[type as DetectionType] = data;
            }
          });
          
          setStats(statsObj);
        }
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch stats');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 10000); // Update every 10 seconds

    return () => clearInterval(interval);
  }, []);

  return { stats, loading, error };
}

export function useAlerts() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setLoading(true);
        const alertsData = await apiService.getAlerts();
        setAlerts(alertsData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch alerts');
      } finally {
        setLoading(false);
      }
    };

    fetchAlerts();
    const interval = setInterval(fetchAlerts, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, []);

  return { alerts, loading, error };
}
